import { firestore } from "firebase/app";
import { db } from "@/main";
import { getClientRef } from "./ClientRepository";
import { ProjectStatusData } from "@/data/models/ProjectStatusData";
import { filterOutUndefined } from "@/utils/Utils";
import {
  ProjectDetails,
  ProjectSyncTarget,
} from "../models/ProjectDetails";
import { iresAxiosInstance } from "@/api/IresApi";
import { parseDate } from "@/utils/DateHelper";
import { ProjectStatusSyncStatus } from "../models/ProjectState";
import { UserModule } from "@/store/modules/UserModule";

function getProjectStatusRef(projectId: string) {
  const id = getClientRef() + "/projectStates/" + projectId;

  return db.doc(id);
}

export async function deleteProjectStep(projectId: string, stepIndex: number) {
  return getProjectStatusRef(projectId).update({
    [`projectSteps.${stepIndex}`]: firestore.FieldValue.delete(),
  });
}

export async function storeProjectState(
  projectId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectState: any,
) {
  return getProjectStatusRef(projectId)
    .set(filterOutUndefined(projectState), { merge: true });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getMaxCreationTime(step: any) {
  let latestCreationTime = -1;

  if (step) {
    if (Array.isArray(step)) {
      for (const stepOfArray of step) {
        const date = parseDate(stepOfArray.creationTimestamp);
        const appointmentCreationTimestamp = parseDate(stepOfArray.appointmentCreationTimestamp);

        if (date && date.getTime() > latestCreationTime) {
          latestCreationTime = date.getTime();
        }

        if (appointmentCreationTimestamp && appointmentCreationTimestamp.getTime() > latestCreationTime) {
          latestCreationTime = appointmentCreationTimestamp.getTime();
        }
      }
    } else {
      const date = parseDate(step.creationTimestamp);
      const appointmentCreationTimestamp = parseDate(step.appointmentCreationTimestamp);

      if (date && date.getTime() > latestCreationTime) {
        latestCreationTime = date.getTime();
      }

      if (appointmentCreationTimestamp && appointmentCreationTimestamp.getTime() > latestCreationTime) {
        latestCreationTime = date.getTime();
      }
    }
  }

  return latestCreationTime;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getProjectStepStatus(stepIndex: number, step: any, data: any) {
  if (!data) {
    return 0;
  }

  let latestStepIndex = stepIndex.toString();
  let maxCreationTime = getMaxCreationTime(step);

  const projectSteps = data.projectSteps;

  for (const stepKey of Object.keys(projectSteps)) {
    const step = projectSteps[stepKey];

    const creationTime = getMaxCreationTime(step);

    if (creationTime > maxCreationTime) {
      latestStepIndex = stepKey;
      maxCreationTime = creationTime;
    }
  }

  return parseInt(latestStepIndex);
}

export interface OtherSteps {
  stepIndex: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

interface SaveProjectStepOptions {
  currentData?: ProjectStatusData;
  otherSteps?: OtherSteps[];
  keepExistingStep?: boolean;
  replaceArrayEntries?: boolean;
}

export async function saveProjectStep(
  project: ProjectDetails,
  stepIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectStep: any,
  updateProjectStatus: boolean,
  saveProjectStepOptions?: SaveProjectStepOptions,
  syncDataStatus?: boolean,
) {
  const { currentData, otherSteps, keepExistingStep, replaceArrayEntries } =
    saveProjectStepOptions ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const syncStepsStatusUpdate: any = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const syncDataStatusUpdate: any = {};

  if (project.syncTarget) {
    project.syncTarget.syncTargetIds.forEach((syncTargetId) => {
      if (syncDataStatus) {
        syncDataStatusUpdate[syncTargetId] = ProjectStatusSyncStatus.NEEDS_SYNC;
      }
      else {
        syncStepsStatusUpdate[syncTargetId] = ProjectStatusSyncStatus.NEEDS_SYNC;
      }
    });
  }

  const projectIdIres = project.clientData.projectNumberIres;

  if (!projectIdIres) {
    return;
  }

  const projectId = project.id;
  const projectStateUpdate = filterOutUndefined({
    syncDataStatus: project.syncTarget ? syncDataStatusUpdate : undefined
  });
  
  const projectStatusData =
    currentData ??
    ((await getProjectStatusRef(project.id).get()).data() as ProjectStatusData);

  if (keepExistingStep && projectStatusData.projectSteps[`${stepIndex}`]) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentStep: any[] =
    projectStatusData.projectSteps[`${stepIndex}`] ?? [];

  const wrapInArray =
    (projectStep.contactPersonNumber ||
      projectStep.contactPersonNumber === 0) &&
    !Array.isArray(projectStep);

  const addPreviousSteps = stepIndex === 9 && projectStatusData.projectSteps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const previousSteps: any = {};

  if (addPreviousSteps) {
    Object.keys(projectStatusData.projectSteps).forEach((key) => {
      if (key !== "9") {
        previousSteps[key] = projectStatusData.projectSteps[key];
      }
    });
    
    previousSteps[stepIndex] = wrapInArray
      ? replaceArrayEntries
        ? [...[projectStep]]
        : [...currentStep, ...[projectStep]]
      : projectStep
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const projectStepsUpdate: any = addPreviousSteps
    ? previousSteps
    : {
        [stepIndex]: wrapInArray
          ? replaceArrayEntries
            ? [...[projectStep]]
            : [...currentStep, ...[projectStep]]
          : projectStep,
      };

  if (otherSteps) {
    for (const otherStep of otherSteps) {
      projectStepsUpdate[otherStep.stepIndex] = otherStep.data;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {
    projectIdIres: projectIdIres,
    projectIdErp: project.clientData.projectNumber,
    projectId: project.id,
    ...filterOutUndefined({
      syncStepsStatus: project.syncTarget ? syncStepsStatusUpdate : undefined,
      projectSteps: projectStepsUpdate,
    }),
  };

  if (updateProjectStatus) {
    payload.projectStatus = getProjectStepStatus(
      stepIndex,
      projectStep,
      currentData
    );
  }

  const response = await iresAxiosInstance.put(
    `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/projectStatus`,
    payload,
    {
      params: {
        projectIdIres: projectIdIres,
      },
    }
  );

  if (
    response.status === 200 &&
    syncDataStatus && 
    Object.keys(syncDataStatusUpdate).length !== 0 &&
    Object.keys(projectStateUpdate).length !== 0
  ) {
    await storeProjectState(projectId, projectStateUpdate);
  }
}

export function createStepDoneData(contactPersonNumber?: number) {
  return {
    createdBy: UserModule.activeUserIdentifier,
    creationTimestamp: new Date().toISOString(),
    ...(contactPersonNumber !== undefined
      ? {
          contactPersonNumber: contactPersonNumber,
        }
      : 0),
  };
}

export async function updateSyncTargets(
  projectId: string, 
  syncTarget: ProjectSyncTarget,
  syncDataStatus?: boolean,
  syncStepsStatus?: boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const syncDataStatusUpdate: any = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const syncStepsStatusUpdate: any = {};

  for (const syncTargetId of syncTarget.syncTargetIds) {
    if (syncDataStatus) {
      syncDataStatusUpdate[syncTargetId] = ProjectStatusSyncStatus.NEEDS_SYNC;
    }
    if (syncStepsStatus) {
      syncStepsStatusUpdate[syncTargetId] = ProjectStatusSyncStatus.NEEDS_SYNC;
    }
  }

  const projectStateUpdate = filterOutUndefined({
    syncDataStatus: syncDataStatus ? syncDataStatusUpdate : undefined,
    syncStepsStatus: syncStepsStatus ? syncStepsStatusUpdate : undefined
  });

  await storeProjectState(projectId, projectStateUpdate);
}


export async function setProjectStepDone(
  project: ProjectDetails,
  stepIndex: number,
  contactPersonNumber?: number
) {
  const stepData = createStepDoneData(contactPersonNumber);
  return await saveProjectStep(project, stepIndex, stepData, true, {
    replaceArrayEntries: true,
  });
}

export function observeProjectStatus(
  projectId: string,
  onNext: (projectStatusData: ProjectStatusData) => void
) {
  onNext({
    projectId: projectId,
    projectIdIres: "",
    projectIdErp: "",
    projectStatus: 0,
    projectSteps: {},
  });

  return getProjectStatusRef(projectId).onSnapshot((snapshot) => {
    onNext(snapshot.data() as ProjectStatusData);
  });
}

export async function saveContactPersonAppointment(
  project: ProjectDetails,
  stepIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectStep: any,
  updateProjectStatus: boolean,
  currentData?: ProjectStatusData
) {
  const projectStatusData =
    currentData ??
    ((await getProjectStatusRef(project.id).get()).data() as ProjectStatusData);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentStep: any[] = projectStatusData.projectSteps[`${stepIndex}`];

  const contactPersonNumber = projectStep.contactPersonNumber;
  /* const isAppointmentForAllContactPersons = contactPersonNumber === 0; */

  if (!projectStep.completed) {
    projectStep["completed"] = false;
  }

  if (currentStep) {
    /* if (isAppointmentForAllContactPersons) {

      return saveProjectStep(
        project,
        stepIndex,
        [
          ...currentStep.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (value: any) =>
              value.completed &&
              (
                value.creationTimestamp != projectStep.creationTimestamp ||
                value.appointmentCreationTimestamp != projectStep.appointmentCreationTimestamp
              )
          ),
          ...[projectStep],
        ],
        updateProjectStatus,
        {
          currentData: currentData,
        }
      );
    } else {
      return saveProjectStep(
        project,
        stepIndex,
        [
          ...currentStep.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (value: any) =>
              (
                value.creationTimestamp != projectStep.creationTimestamp ||
                value.appointmentCreationTimestamp != projectStep.appointmentCreationTimestamp
              ) &&
              (value.contactPersonNumber !== contactPersonNumber ||
                value.completed)
          ),
          ...[projectStep],
        ],
        updateProjectStatus,
        {
          currentData: currentData,
        }
      );
    } */
    
    const cs = currentStep.find((step) => step.contactPersonNumber === contactPersonNumber);
    if (cs?.creationTimestamp) projectStep["creationTimestamp"] = cs.creationTimestamp;
    if (cs?.createdBy) projectStep["createdBy"] = cs.createdBy;

    return saveProjectStep(
      project,
      stepIndex,
      [
        ...currentStep.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (value: any) =>
            value.contactPersonNumber !== contactPersonNumber
        ),
        ...[projectStep],
      ],
      updateProjectStatus,
      {
        currentData: currentData,
      }
    );

  } else {
    return saveProjectStep(
      project,
      stepIndex,
      [projectStep],
      updateProjectStatus,
      {
        currentData: currentData,
      }
    );
  }
}

export async function deleteAppointment(
  projectDetails: ProjectDetails,
  stepIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appointment: any
): Promise<{ status: number; data: string | undefined }> {
  const projectIdIres = projectDetails.clientData.projectNumberIres;

  if (!projectIdIres) {
    return { status: 0, data: undefined };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deletedAppointment: any = { stepIndex };

  // For CP-specific steps (≥ 10), include contactPersonNumber
  if (stepIndex >= 10) {
    deletedAppointment.contactPersonNumber = appointment?.contactPersonNumber ?? 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {
    projectIdIres: projectIdIres,
    projectIdErp: projectDetails.clientData.projectNumber,
    projectId: projectDetails.id,
    projectSteps: {},
    deletedAppointment,
  };

  const response = await iresAxiosInstance.put(
    `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/projectStatus`,
    payload,
    {
      params: {
        projectIdIres: projectIdIres,
      },
      validateStatus: () => true,
    }
  );

  return { status: response.status, data: response.data };
}

export async function updateProjectStepsOnContactPersonDeletion(
  projectId: string,
  toDeleteContactPersonNumber: number,
  syncTarget?: ProjectSyncTarget,
) {
  const projectStatusRef = getProjectStatusRef(projectId);
  const projectStatusData = (await projectStatusRef.get()).data() as ProjectStatusData;
  const projectSteps = projectStatusData?.projectSteps;

  const stepsToUpdate = [10, 11, 14, 15];

  if (projectSteps) {
    stepsToUpdate.forEach((stepIndex: number) => {
      const step = projectSteps[stepIndex.toString()];

      if (step && Array.isArray(step)) {
        const firstContactPersonStep = step.find((it) => it.contactPersonNumber === 1);
        const contactPersonStepToBeDeleted = step.find((it) => it.contactPersonNumber === toDeleteContactPersonNumber);

        switch (stepIndex) {
          case 10: 
          case 14: {
            if (firstContactPersonStep && contactPersonStepToBeDeleted) {
              if (
                parseDate(firstContactPersonStep.creationTimestamp).getMilliseconds() > parseDate(contactPersonStepToBeDeleted.creationTimestamp).getMilliseconds()
              ) {
                firstContactPersonStep.creationTimestamp = contactPersonStepToBeDeleted.creationTimestamp;
                firstContactPersonStep.appointmentDate = contactPersonStepToBeDeleted.appointmentDate
              }
              
              const indexToDelete = step.indexOf(contactPersonStepToBeDeleted);
              if (indexToDelete > -1) {
                step.splice(indexToDelete, 1);
              }
            }
            else if (contactPersonStepToBeDeleted) {
              contactPersonStepToBeDeleted.contactPersonNumber = 1;
            }

            break;
          }

          case 11:
          case 15: {
            if (firstContactPersonStep && contactPersonStepToBeDeleted) {
              if (
                parseDate(firstContactPersonStep.creationTimestamp).getMilliseconds() < parseDate(contactPersonStepToBeDeleted.creationTimestamp).getMilliseconds()
              ) {
                firstContactPersonStep.creationTimestamp = contactPersonStepToBeDeleted.creationTimestamp;
              }
                
              const indexToDelete = step.indexOf(contactPersonStepToBeDeleted);
              if (indexToDelete > -1) {
                step.splice(indexToDelete, 1);
              }
            }
            else if (contactPersonStepToBeDeleted) {
              contactPersonStepToBeDeleted.contactPersonNumber = 1;
            }

            break;
          }
        }
      }
    });

    const promises = [projectStatusRef.set({ projectSteps }, { merge: true })];

    if (syncTarget) {
      promises.push(updateSyncTargets(projectId, syncTarget, true));
    }

    return Promise.all(promises);
  }
}
